import * as React from 'react';
import { StylesConfig, DropdownIndicatorProps, components } from 'react-select';
import AsyncSelect from 'react-select/async';

import { SchoolOption, SchoolNameAutocompleteProps } from './types';

/**
 * Custom dropdown indicator matching existing select arrow style
 */
const CustomDropdownIndicator = (props: DropdownIndicatorProps<SchoolOption, false>) => {
  return (
    <components.DropdownIndicator {...props}>
      <div
        style={{
          width: '10px',
          height: '10px',
          borderBottom: '1px solid #696761', // $keyGry
          borderRight: '1px solid #696761', // $keyGry
          transform: 'rotate(45deg)',
          marginRight: '5px',
          marginBottom: '3px'
        }}
      />
    </components.DropdownIndicator>
  );
};

/**
 * 学校名予測入力コンポーネント (Shinsotsu Common用)
 */
const SchoolNameAutocomplete: React.FC<SchoolNameAutocompleteProps> = ({
  schoolType,
  value,
  onChange,
  isDisabled = false,
  hasError = false
}) => {
  /**
   * ProfileのschoolTypeをSchoolMasterのschoolTypeに変換
   */
  const convertSchoolType = (profileSchoolType: string): string => {
    const mapping: { [key: string]: string } = {
      '1': '0', // university
      '2': '1', // junior_college
      '3': '3' // agricultural_college
    };
    return mapping[profileSchoolType] || profileSchoolType;
  };

  const loadOptions = async (inputValue: string): Promise<SchoolOption[]> => {
    if (!inputValue || inputValue.length < 2) {
      return [];
    }

    try {
      const schoolMasterType = convertSchoolType(schoolType);
      const response = await fetch(
        `/api/school_masters/search?query=${encodeURIComponent(inputValue)}&school_type=${schoolMasterType}`
      );

      if (!response.ok) {
        console.error('School search API error:', response.statusText);
        return [];
      }

      const data: SchoolOption[] = await response.json();
      return data;
    } catch (error) {
      console.error('School search error:', error);
      return [];
    }
  };

  // Check if value is set to determine background color
  const hasValue = value !== null && value !== undefined;

  const customStyles: StylesConfig<SchoolOption> = {
    control: (base, state) => ({
      ...base,
      border: hasError ? '1px solid #e74c3c' : '1px solid #dbd9d9',
      borderRadius: '2px',
      boxShadow: hasError ? '0 0 0 1px #e74c3c' : state.isFocused ? '0 0 0 1px #009800' : 'none',
      '&:hover': {
        borderColor: hasError ? '#e74c3c' : '#dbd9d9'
      },
      minHeight: '45px',
      fontSize: '16px',
      backgroundColor: hasError ? '#fdcdc2' : hasValue ? '#fff' : '#feeeeb'
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '2px 8px'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#999'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#f0f8f0' : 'white',
      color: '#333',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#e0f0e0'
      }
    }),
    menu: (base) => ({
      ...base,
      zIndex: 100
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '8px'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    })
  };

  return (
    <AsyncSelect<SchoolOption>
      cacheOptions
      defaultOptions={false}
      loadOptions={loadOptions}
      onChange={onChange}
      value={value}
      isDisabled={isDisabled}
      placeholder="例) 〇〇大学"
      noOptionsMessage={({ inputValue }) => {
        if (!inputValue || inputValue.length < 2) {
          return '2文字以上入力してください';
        }
        return '該当する学校が見つかりませんでした';
      }}
      loadingMessage={() => '検索中...'}
      styles={customStyles}
      components={{ DropdownIndicator: CustomDropdownIndicator }}
      isClearable
    />
  );
};

export default SchoolNameAutocomplete;
